import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ActiveQuotationForm from "../components/quotation/ActiveQuotationForm";
import { PersistentPageLoading } from "../components/ui/page-structure";
import { createQuotationDraft, quotationDetailDraft } from "../lib/activeQuotation";
import { formatApiError } from "../lib/api";
import {
  downloadQuotationPdf,
  generateQuotationPdf,
  getDeletedQuotation,
} from "../lib/quotationApi";

function deletedHistoryReturn(search) {
  const value = new URLSearchParams(search).get("return");
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/quotations/deleted";
  try {
    const parsed = new URL(value, "http://flowpi.local");
    return parsed.origin === "http://flowpi.local" && parsed.pathname === "/quotations/deleted"
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : "/quotations/deleted";
  } catch {
    return "/quotations/deleted";
  }
}

export default function DeletedQuotationDetail() {
  const { quotationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => createQuotationDraft(""));
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const generateInFlight = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    getDeletedQuotation(quotationId, { signal: controller.signal }).then((detail) => {
      if (id === requestId.current) setDraft(quotationDetailDraft(detail));
    }).catch((requestError) => {
      if (id === requestId.current && requestError?.code !== "ERR_CANCELED") setError(formatApiError(requestError));
    }).finally(() => { if (id === requestId.current) setLoading(false); });
    return () => controller.abort();
  }, [quotationId]);

  if (loading) return <div className="min-w-0 pb-24" data-testid="deleted-quotation-loading"><PersistentPageLoading label="Loading deleted quotation..." data-testid="deleted-quotation-loading-header" /></div>;
  if (error || !draft.quotation) return <div className="mx-auto max-w-[1504px] p-8 text-sm text-destructive" role="alert">{error || "Deleted quotation is unavailable."}</div>;

  const generate = async () => {
    if (generateInFlight.current) return;
    generateInFlight.current = true;
    setGenerating(true);
    try {
      downloadQuotationPdf(await generateQuotationPdf(quotationId));
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      generateInFlight.current = false;
      setGenerating(false);
    }
  };

  return <ActiveQuotationForm
    mode="view"
    draft={draft}
    setDraft={setDraft}
    onBack={() => navigate(deletedHistoryReturn(location.search))}
    onGenerate={generate}
    generating={generating}
    lifecycle="deleted"
  />;
}
